/* eslint-disable unused-imports/no-unused-imports */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import { PrinterOutlined, UploadOutlined } from "@ant-design/icons";
import { BaseKey, GetOneResponse, useNotification } from "@refinedev/core";
import { QueryObserverResult } from "@tanstack/react-query";
import {
  Badge,
  Button,
  Col,
  Form,
  FormProps,
  Input,
  Modal,
  ModalProps,
  Row,
  Typography,
  Upload,
} from "antd";
import moment from "moment";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useChangeStatusPaymentRequest } from "../../api/requestMoney";
import { ROOT_URL } from "../../App";
import { getPurposeOfRemittanceOptionLabel, purposeOfRemittanceItems, purposeOfRemittancePlaceholder } from "../../components/inputs/select/purposeOfRemittance.constants";
import { SelectField } from "../../components/inputs/select/select";
import {
  getStatusOptionLabel,
  paymentOrderInvoiceReceivablePossibleMoves,
} from "../../components/inputs/select/statusSelect.constants";
import TextContent from "../../components/modal/admin/text-content";
import { TOKEN_KEY } from "../../components/providers/authProvider";
import PaymentStatus from "../../types/paymentStatus.type";
import { acceptUploadFileTypes } from "../../utils/acceptUploadFileTypes";
import { beforeUpload } from "../../utils/beforeUpload";
import {
  clearFieldErrorMessageOnChange,
  getFileListFromEvent,
} from "../../utils/form.constants";
import { useIsAdminRole } from "../../utils/hooks/useIdentityAdmin.hook";
import { useNotificationOkCancel } from "../../utils/hooks/useNotificationOkCancel.hook";
import {
  paymentStatusColors,
  paymentStatusNames
} from "../../utils/paymentStatus.utils";
import { confirmModalUnsavedChangesProps } from "../components/constants";
import {
  IDocumentsChanges,
  PaymentOrderEditForm
} from "../components/interface";
import { uploadButtonStyle } from "../styles/style";

interface IProps {
  formProps: FormProps;
  modalProps: ModalProps;
  queryResult?: QueryObserverResult<GetOneResponse<PaymentOrderEditForm>>;
  id?: BaseKey;
  resetEditingId: () => void;
  formIsLoading: boolean;
  close: () => void;
  refetchListRequestMoney: any;
}

export const Fiat = [
  'USD',
  'EUR',
  'HKD',
  'AUD',
  'JPY',
  'CHF',
  'CNY',
  'AED',
  'SGD',
  'GBP',
];
export const Crypto = ["USDT", "DAI", "ETH"];

export const PaymentOrderEdit = ({
  modalProps,
  formProps,
  queryResult,
  id,
  resetEditingId,
  formIsLoading,
  close,
  refetchListRequestMoney,
}: IProps) => {
  const isAdminRole = useIsAdminRole();
  const { open: openNotification } = useNotification();
  const [prevPaymentRequest, setPrevPaymentRequest] = useState<any>();
  const [formChanged, setFormChanged] = useState<boolean>(false);
  const [initialDocuments, setInitialDocuments] = useState<string[]>([]);
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus>(
    PaymentStatus.DRAFT
  );
  const status = Form.useWatch("status", { form: formProps.form });

  const { mutate, isLoading } = useChangeStatusPaymentRequest({
    onSuccess: (response: { data: { message: string } }) => {
      if (openNotification) {
        openNotification({
          key: "success_transaction_confirm",
          message: response?.data?.message || "Change status successfully!",
          type: "success",
        });
      }
      refetchListRequestMoney();
      queryResult?.refetch();
    },
    onError: (error: { message: string; reason_code: string }) => {
      if (openNotification) {
        openNotification({
          key: "error",
          message: error?.message || "Change status failed!",
          type: "error",
        });
      }
    },
  });

  const onSubmit = (formValues: any) => {
    const { documents } = formValues;

    const documentChanges: IDocumentsChanges = {
      new: documents
        ?.filter((doc: { response?: any }) => doc.response)
        .map((doc: { response?: any }) => doc.response ?? ""),
      deleted: initialDocuments?.filter(
        (initialFileName: string) =>
          !documents?.find(({ name }: any) => {
            return name === initialFileName;
          })
      ),
    };

    const documentChanged = !!(
      (documentChanges.new && documentChanges.new.length) ||
      documentChanges?.deleted?.length
    );

    const body = {
      id: id || 0,
      documentChanges: documentChanged ? documentChanges : undefined,
      status: formValues.status,
      reason:
        formValues.status === PaymentStatus.APPROVED ||
          formValues.status === PaymentStatus.PENDING
          ? null
          : formValues.reason,
      purposeOfRemittance: formValues.purposeOfRemittance,
    };
    mutate(body);
  };

  const onClose = useCallback(() => {
    close();
  }, [close]);

  const {
    openNotification: openUnsavedChangesNotification,
    contextHolder: unsavedChangesContextHolder,
  } = useNotificationOkCancel({
    ...confirmModalUnsavedChangesProps,
    onOk: onClose,
  });

  const statusSelectItems = useMemo(
    () =>
      paymentOrderInvoiceReceivablePossibleMoves.get(
        prevPaymentRequest?.status?.current || PaymentStatus.DRAFT
      ) || [PaymentStatus.DRAFT],
    [prevPaymentRequest?.status?.current]
  );

  const onModalCancel = useCallback(() => {
    if (formIsLoading || isLoading) return;
    onClose();
  }, [onClose, formIsLoading, isLoading]);

  const afterClose = useCallback(() => {
    setFormChanged(false);
    resetEditingId();
    setPrevPaymentRequest(undefined);
    queryResult?.remove();
  }, [queryResult, resetEditingId]);

  useEffect(() => {
    if (
      queryResult?.status === "success" &&
      modalProps.open &&
      queryResult?.data?.data
    ) {
      setPrevPaymentRequest(queryResult.data.data);
    }
  }, [queryResult, modalProps.open]);

  useEffect(() => {
    if (prevPaymentRequest) {
      formProps.form?.setFieldsValue({
        status: prevPaymentRequest?.status?.current,
        documents: prevPaymentRequest?.documentNames?.map(
          (fileName: string) => ({
            name: fileName,
          })
        ),
        purposeOfRemittance: prevPaymentRequest?.purposeOfRemittance,
      });
      setInitialDocuments(prevPaymentRequest?.documentNames ?? []);
    }
  }, [formProps.form, prevPaymentRequest]);

  return (
    <>
      <Modal
        {...modalProps}
        className="smartModalTitle"
        title={`Request money #${id ?? prevPaymentRequest?.id}`}
        destroyOnClose
        maskClosable={false}
        footer={
          <Col>
            <Row
              style={{
                justifyContent: "space-around",
                justifyItems: "center",
                width: "400px",
              }}
            >
              <Button
                key="save"
                type={isAdminRole ? "primary" : undefined}
                onClick={() => formProps?.form?.submit()}
                loading={isLoading || formIsLoading}
                disabled={
                  prevPaymentRequest?.status?.current ===
                  PaymentStatus.REJECTED ||
                  prevPaymentRequest?.status?.current ===
                  PaymentStatus.COMPLETED ||
                  prevPaymentRequest?.status?.current ===
                  PaymentStatus.APPROVED ||
                  prevPaymentRequest?.status?.current ===
                  PaymentStatus.PROCESSING ||
                  prevPaymentRequest?.status?.current ===
                  PaymentStatus.CANCELLED ||
                  statusSelectItems.length <= 1 ||
                  isLoading ||
                  formIsLoading
                }
              >
                Save
              </Button>
              <Button
                key="save"
                onClick={onModalCancel}
                loading={isLoading || formIsLoading}
                disabled={isLoading || formIsLoading}
              >
                Cancel
              </Button>
            </Row>
            {prevPaymentRequest?.printableFormUrl && (
              <Button
                icon={<PrinterOutlined />}
                type={"link"}
                href={prevPaymentRequest?.printableFormUrl}
                target={"_blank"}
              >
                &nbsp;Print
              </Button>
            )}
          </Col>
        }
        onCancel={onModalCancel}
        afterClose={afterClose}
      >
        {unsavedChangesContextHolder}
        <Form
          {...formProps}
          onFinish={onSubmit}
          validateTrigger={false}
          onValuesChange={(e) => {
            setFormChanged(true);
            clearFieldErrorMessageOnChange(formProps?.form)(e);
          }}
          className="detail-withdraw"
        >
          <div
            style={{
              display: "flex",
              width: "100%",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "end",
            }}
          >
            <Badge
              color={
                paymentStatusColors[
                prevPaymentRequest?.status?.current || "PENDING"
                ]
              }
              text={
                paymentStatusNames[
                prevPaymentRequest?.status?.current || "PENDING"
                ]
              }
            />
          </div>
          <hr />
          <TextContent
            label="Total to pay"
            value={`${prevPaymentRequest?.amounts?.totalSendAmount} ${prevPaymentRequest?.paymentCurrency}`}
            boldValue={true}
          />
          {prevPaymentRequest?.remiter?.type === "INTERNAL" ? null : (
            <div
              style={{
                display: "flex",
                width: "100%",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "end",
              }}
            >
              <Typography
                className="text-label"
                style={{
                  textAlign: "start",
                  fontSize: "12px",
                  color:
                    prevPaymentRequest?.isPaid ||
                      prevPaymentRequest?.status?.current ===
                      PaymentStatus.COMPLETED
                      ? "#18B853"
                      : "rgba(242, 138, 40, 1)",
                }}
              >
                {prevPaymentRequest?.isPaid ||
                  prevPaymentRequest?.status?.current === PaymentStatus.COMPLETED
                  ? "*User has paid"
                  : "*User has not paid"}
              </Typography>
            </div>
          )}
          <TextContent
            label="Beneficiary Will Receive"
            value={`${prevPaymentRequest?.amounts?.totalReceiveAmount} ${prevPaymentRequest?.receiveCurrency}`}
            boldValue={true}
          />
          <hr />
          <TextContent label="Payment Method" value="Provider" />
          <TextContent
            label="Provider"
            value={prevPaymentRequest?.serviceProvider?.name || "N/A"}
          />
          <TextContent
            label="Currency"
            value={prevPaymentRequest?.paymentCurrency}
          />
          {Crypto.includes(prevPaymentRequest?.paymentCurrency) ? (
            <>
              <TextContent
                label="Provider Wallet Network"
                value={prevPaymentRequest?.serviceProvider?.network}
              />
              <TextContent
                label="Provider Wallet Address"
                value={prevPaymentRequest?.serviceProvider?.walletAddress}
              />
            </>
          ) : (
            <>
              <TextContent
                label="Provider Bank Name"
                value={prevPaymentRequest?.serviceProvider?.providerBankName}
              />
              <TextContent
                label="Provider Bank SWIFT"
                value={prevPaymentRequest?.serviceProvider?.providerBankSwift}
              />
              <TextContent
                label="Provider Bank Address"
                value={prevPaymentRequest?.serviceProvider?.providerBankAddress}
              />
              <TextContent
                label="Provider Bank Account"
                value={prevPaymentRequest?.serviceProvider?.providerBankAccount}
              />
            </>
          )}
          <TextContent
            label="Reference Number"
            value={prevPaymentRequest?.referenceNumber}
          />
          {/* <TextContent
            label="Send Date"
            value={moment(
              prevPaymentRequest?.timestamps?.dateOfTransaction
            ).format("DD/MM/YYYY")}
          /> */}
          <TextContent
            label="Future date"
            value={
              prevPaymentRequest?.futurePaymentDate
                ? moment(
                  prevPaymentRequest?.futurePaymentDate
                ).format("DD/MM/YYYY")
                : ""
            }
          />
          <hr />
          <TextContent
            label="Order Time"
            value={moment(prevPaymentRequest?.timestamps?.orderTime).format(
              "HH:mm:ss DD/MM/YYYY"
            )}
          />
          <TextContent
            label="Special instructions"
            value={prevPaymentRequest?.specialInstructions || "N/A"}
          />
          {isAdminRole && (
            <SelectField
              label="Purpose Of Remittance"
              name="purposeOfRemittance"
              getOptionLabel={getPurposeOfRemittanceOptionLabel}
              items={purposeOfRemittanceItems}
              placeholder={purposeOfRemittancePlaceholder}
              allowClear
              rules={[{ required: true, message: "This field is required" }]}
              disabled={
                prevPaymentRequest?.status?.current ===
                PaymentStatus.REJECTED ||
                prevPaymentRequest?.status?.current ===
                PaymentStatus.POSTPONED ||
                statusSelectItems.length <= 1 ||
                isLoading ||
                formIsLoading
              }
            />
          )}
          {isAdminRole && (
            <SelectField
              rules={[{ required: true, message: "This field is required" }]}
              label="Status"
              name="status"
              getOptionLabel={getStatusOptionLabel}
              items={statusSelectItems}
              disabled={
                (prevPaymentRequest?.status?.current !==
                  PaymentStatus.PENDING &&
                  prevPaymentRequest?.status?.current !==
                  PaymentStatus.POSTPONED) ||
                isLoading ||
                formIsLoading
              }
            />
          )}
          {status === PaymentStatus?.REJECTED ||
            status === PaymentStatus.POSTPONED ? (
            <Form.Item
              label="Reason"
              name="reason"
              rules={[{ required: true, message: "This field is required" }]}
            >
              <Input.TextArea
                rows={2}
                placeholder="Reason"
                disabled={
                  prevPaymentRequest?.status?.current ===
                  PaymentStatus.REJECTED ||
                  prevPaymentRequest?.status?.current ===
                  PaymentStatus.POSTPONED ||
                  isLoading ||
                  formIsLoading
                }
              />
            </Form.Item>
          ) : null}
          <Col>
            <Form.Item
              name="documents"
              valuePropName="fileList"
              getValueFromEvent={getFileListFromEvent}
            >
              <Upload
                accept={acceptUploadFileTypes}
                action={`${ROOT_URL}/api/v1/storage/upload`}
                listType="text"
                headers={{
                  Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}`,
                }}
                multiple={true}
                beforeUpload={beforeUpload}
                showUploadList={{
                  showRemoveIcon:
                    prevPaymentRequest?.status?.current !==
                      PaymentStatus.DRAFT &&
                      prevPaymentRequest?.status?.current !==
                      PaymentStatus.PENDING
                      ? false
                      : true,
                }}
              >
                <Button
                  icon={<UploadOutlined />}
                  style={uploadButtonStyle}
                  disabled={
                    (prevPaymentRequest?.status?.current !==
                      PaymentStatus.DRAFT &&
                      prevPaymentRequest?.status?.current !==
                      PaymentStatus.PENDING) ||
                    isLoading ||
                    formIsLoading
                  }
                >
                  &nbsp;Click to upload supporting documents
                </Button>
              </Upload>
            </Form.Item>
          </Col>
        </Form>
      </Modal>
    </>
  );
};
